import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown } from 'lucide-react';

const API = (import.meta.env.VITE_API_URL || 'http://64.23.240.10/api/v1').replace('/v1', '');

interface Mover {
  id: number;
  name: string;
  slug: string;
  chain: string;
  previous_apy: string;
  new_apy: string;
  change_percent: string;
  direction: string;
}

export default function TopMovers() {
  const [movers, setMovers] = useState<Mover[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/top-movers`)
      .then(r => r.json())
      .then(d => {
        const unique = (d.data || []).filter((m: Mover, idx: number, arr: Mover[]) =>
          arr.findIndex(x => x.slug === m.slug) === idx
        );
        setMovers(unique.slice(0, 6));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (!loading && movers.length === 0) return null;

  return (
    <div className="mb-16">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold">
          <span className="text-cream">Top </span>
          <span className="gradient-text-neon">Movers</span>
          <span className="text-cream"> (24h)</span>
        </h2>
        <Link to="/leaderboard" className="text-sky-blue hover:text-neon-red transition-colors text-sm">
          View leaderboard →
        </Link>
      </div>
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card-glass h-24 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {movers.map(m => {
            const isUp = m.direction === 'up';
            const change = parseFloat(m.change_percent);
            const newApy = parseFloat(m.new_apy);
            return (
              <Link key={m.id} to={`/protocol/${m.slug}`}
                className="card-glass hover:border-sky-blue/30 transition-all group">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-cta rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-neon shrink-0">
                      {m.name[0]}
                    </div>
                    <div>
                      <div className="text-cream font-semibold text-sm group-hover:text-neon-red transition-colors">{m.name}</div>
                      <div className="text-sky-blue/60 text-xs">{m.chain}</div>
                    </div>
                  </div>
                  {isUp
                    ? <TrendingUp className="w-4 h-4 text-green-400 shrink-0" />
                    : <TrendingDown className="w-4 h-4 text-neon-red shrink-0" />
                  }
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <div className="gradient-text-neon font-bold text-xl">{newApy.toFixed(2)}%</div>
                    <div className="text-sky-blue/60 text-xs">APY</div>
                  </div>
                  <div className={`text-sm font-bold ${isUp ? 'text-green-400' : 'text-neon-red'}`}>
                    {isUp ? '+' : ''}{change.toFixed(1)}%
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
